import React, { FC, ReactNode, useEffect, useState } from 'react';
import { CreateShopFormType, ShopType, ShopPolygonType } from 'utils/types';
import { getWorkingHoursComponentFormatFromApi, handleApiError } from 'utils/functions';
import { EditShopContext } from './context';
import { apiService } from 'services/base.service';
import { useParams } from 'react-router-dom';
import moment from 'moment';
import { days } from 'utils/constants';
import { MetadataType } from 'pages/create-brand/create-brand-metadata/types';
import { toast } from 'react-toastify';
import { ToastMessage } from '@cokitchen/cokitchen-components';
import useDashboardContext from 'contexts/dashboard.context';
import { CreateMetadataResponse } from 'pages/metadata/types';

const CREATE_ENDPOINT = {
  brand: 'keywords/brand_keyword',
  descriptive: 'keywords/brand_descriptive_metadata',
  business: 'keywords/brand_business_metadata'
};

const EditShopProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { shop_id } = useParams();
  const daysArray = getWorkingHoursComponentFormatFromApi(days());
  const [shopDetails, setShopDetails] = useState<CreateShopFormType>({
    brand_keywords: [],
    brand_business_metadatas: [],
    brand_descriptive_metadatas: [],
    brand_working_hours: daysArray,
    name: '',
    description: '',
    summary: '',
    pop_up: false,
    images: [],
    popup_color: '',
    short_text: '',
    logo: '',
    phone: '',
    polygons: [],
    brandStatustext: '',
    shopStatus: [],
    email: '',
    address: {
      longitude: 0,
      latitude: 0,
      city: '',
      state: '',
      country: '',
      country_code: '',
      address_line: ''
    },
    platform_fee: 0,
    platform_fee_cap: 0,
    service_fee: 0,
    service_fee_cap: 0,
    shop_polygons: []
  });
  const [polygons, setPolygons] = useState<ShopPolygonType[]>([]);
  const [disabled, setDisabled] = useState(false);
  const [loadingShop, setLoadingShop] = useState(false);
  const { category } = useDashboardContext();

  const [keywordsList, setKeywordsList] = useState<MetadataType[]>([]);
  const [descriptiveMetadataList, setDescriptiveMetadataList] = useState<MetadataType[]>([]);
  const [businessMetadataList, setBusinessMetadataList] = useState<MetadataType[]>([]);

  const MAP_LOCAL_STATE = {
    brand: setKeywordsList,
    descriptive: setDescriptiveMetadataList,
    business: setBusinessMetadataList
  };

  const createMetadata = async (name: string, type: keyof typeof CREATE_ENDPOINT) => {
    const API_URL = CREATE_ENDPOINT[type];
    try {
      const res = await apiService.post(API_URL, {
        data: {
          name,
          item_type: category?.toUpperCase()
        }
      });
      const { message, id } = res.data as CreateMetadataResponse;
      toast(<ToastMessage type='success' message={message} />);
      MAP_LOCAL_STATE[type]((p) => [{ name, id }, ...p]);
    } catch {
      toast(<ToastMessage type='error' message='There was a problem adding metadata' />);
    }
  };

  const getKeywordList = async () => {
    try {
      const [resBrand, resBusiness, resDes] = await Promise.all([
        apiService.get(`${CREATE_ENDPOINT.brand}`),
        apiService.get(`${CREATE_ENDPOINT.business}`),
        apiService.get(`${CREATE_ENDPOINT.descriptive}`)
      ]);

      const brandMetadata: MetadataType[] = resBrand.data.data;
      const businessMetadata: MetadataType[] = resBusiness.data.data;
      const descriptiveMetadata: MetadataType[] = resDes.data.data;

      setKeywordsList(brandMetadata);
      setDescriptiveMetadataList(descriptiveMetadata);
      setBusinessMetadataList(businessMetadata);
    } catch (error) {
      toast(<ToastMessage type='error' message={'Failed to fetch'} />);
    }
  };

  useEffect(() => {
    getKeywordList();
  }, []);

  const getShopById = async () => {
    setLoadingShop(true);
    if (!shop_id) return;
    try {
      const res = await apiService.get(`shops/${shop_id}`);
      const singleShopData = res.data.data as ShopType;
      setPolygons(singleShopData.polygons);
      setShopDetails({
        ...shopDetails,
        brand_business_metadatas: singleShopData.business_meta.map((data) => ({
          id: data.id,
          name: data.name
        })),
        brand_descriptive_metadatas: singleShopData.descriptive_meta.map((data) => ({
          id: data.id,
          name: data.name
        })),
        brand_keywords: singleShopData.keyword_meta.map((data) => ({
          id: data.id,
          name: data.name
        })),
        description: singleShopData.description,
        images: singleShopData.images,
        phone: singleShopData.phone_numbers[0],
        logo: singleShopData.logo,
        email: singleShopData.email,
        address: singleShopData.address,
        shop_polygons: singleShopData.polygons,
        short_text: singleShopData.tagline,
        platform_fee: singleShopData.platform_fee,
        platform_fee_cap: singleShopData.platform_fee_cap,
        service_fee: singleShopData.service_fee,
        service_fee_cap: singleShopData.service_fee_cap,
        polygons: singleShopData.polygons.map((pol) => ({
          id: pol.id,
          name: pol.name,
          polygon: pol.polygon
        })),
        brand_working_hours: singleShopData?.opening_hours?.map((data) => ({
          ...data,
          opening_time: {
            amPm: moment(data.open_time, 'HH:mm:ss').format('a').toUpperCase(),
            hours: moment(data.open_time, 'HH:mm:ss').format('hh'),
            minutes: moment(data.open_time, 'HH:mm:ss').format('mm'),
            timeWithColon: moment(data.open_time, 'HH:mm:ss').format('hh:mm'),
            totalHours: '12'
          },
          closing_time: {
            amPm: moment(data.close_time, 'HH:mm:ss').format('a').toUpperCase(),
            hours: moment(data.close_time, 'HH:mm:ss').format('hh'),
            minutes: moment(data.close_time, 'HH:mm:ss').format('mm'),
            timeWithColon: moment(data.close_time, 'HH:mm:ss').format('hh:mm'),
            totalHours: '12'
          },
          name: data?.day
        })),
        name: singleShopData.name,
        shopStatus: singleShopData.shop_info.filter((data) => data.active).map((data) => data.name),
        brandStatustext:
          singleShopData.shop_info.find((data) => data.name === 'coming_soon')?.note ?? '',
        live: singleShopData.status
      });
    } catch (err: any) {
      handleApiError(err);
    } finally {
      setLoadingShop(false);
    }
  };

  useEffect(() => {
    if (shop_id) getShopById();
  }, [shop_id]);

  return (
    <EditShopContext.Provider
      value={{
        shopDetails,
        setShopDetails,
        disabled,
        setDisabled,
        polygons,
        setPolygons,
        keywordsList,
        descriptiveMetadataList,
        businessMetadataList,
        setKeywordsList,
        setDescriptiveMetadataList,
        setBusinessMetadataList,
        createMetadata,
        loadingShop,
        getShopById
      }}>
      {children}
    </EditShopContext.Provider>
  );
};

export default EditShopProvider;
